'use client'

import { SessionProvider } from 'next-auth/react'
import { ThemeProvider } from 'next-themes'
import React, { ReactNode, useEffect, useMemo } from 'react'
import { parseTheme } from 'app/lib/parseConfigTheme'
import { rgbaToHsla } from 'lib/rgbaToHsla'
import { removeThemeInlineStyle } from 'app/lib/rootLayoutUtils'
import { DEFAULT_DATA_THEME } from 'app/config/disporty'

interface RootProvidersProps {
    children: ReactNode
    config: IDomainConfig
}

const setThemeProperty = (name: string, value: { color: string } | undefined) => {
    if (value?.color) {
        document.documentElement.style.setProperty(name, rgbaToHsla(value.color))
    }
}

/** CSS variables set for custom theme – must be cleared when switching to preset so [data-theme] rules apply */
const CUSTOM_THEME_VARS = [
    '--background',
    '--muted-foreground',
    '--foreground',
    '--card-foreground',
    '--card',
    '--popover-foreground',
    '--primary-foreground',
    '--secondary-foreground',
    '--accent-foreground',
    '--destructive-foreground',
    '--warning-foreground',
    '--info-foreground',
    '--success-foreground',
    '--primary',
    '--secondary',
    '--muted',
    '--accent',
    '--popover',
    '--destructive',
    '--border',
    '--input',
    '--ring',
    '--info',
    '--success',
    '--warning'
]

function clearCustomThemeVars() {
    CUSTOM_THEME_VARS.forEach(name => document.documentElement.style.removeProperty(name))
}

const RootProviders: React.FC<RootProvidersProps> = ({ children, config }) => {
    const defaultTheme = useMemo(() => {
        if (!config?.theme) return DEFAULT_DATA_THEME
        const { branding } = parseTheme(config.theme) || {}
        return branding?.use_preset_theme && branding?.preset_theme
            ? branding.preset_theme
            : DEFAULT_DATA_THEME
    }, [config?.theme])

    useEffect(() => {
        const cardData = parseTheme(config?.theme)
        const branding = cardData?.branding

        if (branding) {
            const { use_preset_theme, preset_theme } = branding

            if (use_preset_theme && preset_theme) {
                clearCustomThemeVars()
                removeThemeInlineStyle()
                document.documentElement.setAttribute('data-theme', preset_theme)
            } else {
                const {
                    page_bg,
                    secondary_bg,
                    primary,
                    text,
                    info,
                    success,
                    error,
                    warning,
                    softHighlight,
                    ring,
                    border,
                    input,
                    soft_text,
                    card,
                    is_gradient_background
                } = branding

                if (is_gradient_background) {
                    document.documentElement.style.setProperty('--background', page_bg?.color ?? '')
                } else {
                    setThemeProperty('--background', page_bg)
                }

                // Set other theme properties
                setThemeProperty('--muted-foreground', soft_text)
                setThemeProperty('--foreground', text)
                setThemeProperty('--card-foreground', text)
                setThemeProperty('--card', card)
                setThemeProperty('--popover-foreground', text)
                setThemeProperty('--primary-foreground', text)
                setThemeProperty('--secondary-foreground', text)
                setThemeProperty('--accent-foreground', text)
                setThemeProperty('--destructive-foreground', text)
                setThemeProperty('--warning-foreground', text)
                setThemeProperty('--info-foreground', text)
                setThemeProperty('--success-foreground', text)
                setThemeProperty('--primary', primary)
                setThemeProperty('--secondary', secondary_bg)
                setThemeProperty('--muted', softHighlight)
                setThemeProperty('--accent', softHighlight)
                setThemeProperty('--popover', secondary_bg)
                setThemeProperty('--destructive', error)
                setThemeProperty('--border', border)
                setThemeProperty('--input', input)
                setThemeProperty('--ring', ring)
                setThemeProperty('--info', info)
                setThemeProperty('--success', success)
                setThemeProperty('--warning', warning)
            }
        }
    }, [config?.theme])

    return (
        <ThemeProvider attribute='class' defaultTheme={defaultTheme} enableSystem={false}>
            <SessionProvider>
                {children}
            </SessionProvider>
        </ThemeProvider>
    )
}

export default RootProviders
